import { Dialog } from '@spiaggeit/spit-ui'
import { decode } from 'html-entities'
import { ComponentPropsWithoutRef } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { useAppSelector } from '@/hooks/store'
import { DialogProps } from '@/models/dialog-props'
import { BookingRefundPolicy } from '@/models/license'
import { isKioskSelector } from '@/store/appSlice'
import { bookingPolicyStringSelector } from '@/store/licenseSlice/selectors/bookingPolicyStringSelector'
import { isLoadingLicenseStringSelector } from '@/store/licenseSlice/selectors/isLoadingLicenseStringsSelector'
import { REFUND_POLICY_DAYS_MAP } from '@/utils/bookingRefundPolicy'

export interface RefundPolicyDialogProps extends DialogProps {
  bookingRefundPolicy?: BookingRefundPolicy
  licenseName: string
}

export const RefundPolicyDialog = (props: RefundPolicyDialogProps) => {
  const {
    bookingRefundPolicy: days = BookingRefundPolicy.NEVER,
    licenseName,
    ...dialogProps
  } = props
  const isKiosk = useAppSelector(isKioskSelector)
  const { t } = useTranslation()
  const mappedDays = REFUND_POLICY_DAYS_MAP[days]
  const paragraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: decode(
        t('bookingConditions.refundableByVoucherDescription', {
          days: mappedDays,
          licenseName,
        })
      ),
      title: t('bookingConditions.refundableByVoucherTitle'),
    },
    {
      description: t('bookingConditions.refundTimetableDescription', {
        days: mappedDays,
      }),
      title: t('bookingConditions.refundTimetableTitle'),
    },
  ]

  const neverParagraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: t('bookingConditions.advancePaymentDescriptionNever'),
      title: t('bookingConditions.refundableByVoucherTitleNever'),
    },
    {
      description: t('bookingConditions.refundTimetableDescriptionNever'),
      title: t('bookingConditions.refundTimetableTitleNever'),
    },
  ]

  const dayBeforeParagraphs = [
    {
      description: t('bookingConditions.advancePaymentDescription'),
      title: t('bookingConditions.advancePaymentTitle'),
    },
    {
      description: t(
        'bookingConditions.refundableByVoucherDescriptionDayBefore',
        {
          licenseName,
        }
      ),
      title: t('bookingConditions.refundableByVoucherTitle'),
    },
    {
      description: t('bookingConditions.refundTimetableDescriptionDayBefore'),
      title: t('bookingConditions.refundTimetableTitle'),
    },
  ]

  const visibleParagraphs = (() => {
    if (mappedDays === REFUND_POLICY_DAYS_MAP[BookingRefundPolicy.NEVER]) {
      return neverParagraphs
    } else if (
      mappedDays === REFUND_POLICY_DAYS_MAP[BookingRefundPolicy.DAY_BEFORE]
    ) {
      return dayBeforeParagraphs
    } else {
      return paragraphs
    }
  })()

  const bookingPolicyText = useAppSelector(bookingPolicyStringSelector)
  const isLoadingStrings = useAppSelector(isLoadingLicenseStringSelector)

  if (isLoadingStrings) {
    return (
      <>
        <LoaderTrigger />
        <p>{t('bookingConditions.title')}</p>
      </>
    )
  }

  return (
    <Dialog
      scrollContent
      size={isKiosk ? null : 'sm'}
      title={t('bookingConditions.title')}
      {...dialogProps}
    >
      {bookingPolicyText ? (
        <Content
          dangerouslySetInnerHTML={{
            __html: bookingPolicyText.replace(/\n/g, '<br />'),
          }}
        />
      ) : (
        <Content>
          {visibleParagraphs.map((paragraph) => (
            <div className="pb-4" key={paragraph.title}>
              <h3 className="font-bold text-primary">{paragraph.title}</h3>
              <p className="text-secondary">{paragraph.description}</p>
            </div>
          ))}
        </Content>
      )}
    </Dialog>
  )
}

const Content = (props: ComponentPropsWithoutRef<'div'>) => {
  const { children, ...rest } = props

  return (
    <div className="p-4" {...rest}>
      {children}
    </div>
  )
}
